/**
 * §6.E "Schedule a game" deep-link capture. A host's share link is
 * `https://play.goodtime.casino/?game=CODE`; on launch the app reads that code
 * and shows the {@code ScheduledGameCard} (the first thing an invited friend
 * sees). Mirrors the room-invite reader in {@link ./joinInvite} and the
 * recovery-token reader in {@link ./recoveryToken}.
 *
 * <p>Like the join code, the value is stashed in localStorage so it survives a
 * launch round-trip (the address bar is stripped immediately so a refresh can't
 * re-trigger). A scheduled game has a real start time and can be revisited up to
 * then, so the TTL is generous — but bounded, so a long-stale link doesn't haunt
 * a much later session.
 */

const PENDING_KEY = 'goodtime.game.pending';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7d — a scheduled game won't outlive this

interface Pending {
  code: string;
  ts: number;
}

function stash(code: string): void {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ code, ts: Date.now() } satisfies Pending),
    );
  } catch {
    /* private mode — the URL param still covers the no-round-trip case */
  }
}

/**
 * Read a scheduled-game code at launch — from `?game=CODE` — strip it from the
 * address bar so a refresh can't re-trigger, and stash it so it survives a
 * round-trip that drops the query string. Falls back to a still-valid stashed
 * code when the URL has none. Returns the code (preserved as sent — these are
 * server-minted, mixed-case-safe), or null. Call once at startup.
 */
export function readLaunchGameCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const u = new URL(window.location.href);
    const fromUrl = u.searchParams.get('game');
    if (fromUrl) {
      u.searchParams.delete('game');
      const qs = u.searchParams.toString();
      window.history.replaceState({}, '', u.pathname + (qs ? `?${qs}` : ''));
      const code = fromUrl.trim();
      if (code) {
        stash(code);
        return code;
      }
    }

    // No code in the URL — fall back to a still-valid stashed one.
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const { code, ts } = JSON.parse(raw) as Pending;
    if (!code || typeof ts !== 'number' || Date.now() - ts > TTL_MS) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

/** Clear the pending game code after the card is dismissed / joined, so a
 *  refresh won't re-surface it. */
export function clearPendingGameCode(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
